import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Navbar } from '@/components/Navbar';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Wifi, Radio, Send, Trash2, WifiOff, MessageSquare, Hand, Mic, MicOff, PhoneOff, X, Video, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BrandWatermark } from '@/components/BrandWatermark';
import { ZoomEmbed } from '@/components/ZoomEmbed';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MUX_PLAYBACK_ID = "c1p00H6YeJARlyxWAeHuis9vDlNJof2gkJTRE1wWTOjM";

function timeAgo(dateStr) {
  const diff = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h`;
}

const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }];

function HandRaisePanel({ isAdmin }) {
  const { user } = useAuth();
  const [handRaised, setHandRaised] = useState(false);
  const [raises, setRaises] = useState([]);
  const [activeCall, setActiveCall] = useState(null);
  const [muted, setMuted] = useState(false);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const pollingRef = useRef(null);

  // Poll hand raises
  useEffect(() => {
    const fetchRaises = async () => {
      try {
        const res = await axios.get(`${API}/live/hand-raises`);
        setRaises(res.data);
        // Check if my hand raise was approved (student side)
        if (!isAdmin && handRaised) {
          const myRaise = res.data.find(r => r.user_id === user?.id);
          if (myRaise?.status === 'approved' && !activeCall) {
            startListeningForOffer();
          }
          if (!myRaise) {
            setHandRaised(false);
            cleanupCall();
          }
        }
      } catch {}
    };
    fetchRaises();
    const interval = setInterval(fetchRaises, 3000);
    return () => clearInterval(interval);
  }, [handRaised, isAdmin, activeCall]);

  // Poll for WebRTC signals
  const pollSignals = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/live/rtc/signals`);
      for (const sig of res.data) {
        if (sig.signal_type === 'offer') {
          await handleOffer(JSON.parse(sig.data), sig.from_user);
        } else if (sig.signal_type === 'answer') {
          await handleAnswer(JSON.parse(sig.data));
        } else if (sig.signal_type === 'ice') {
          await handleIce(JSON.parse(sig.data));
        }
      }
    } catch {}
  }, []);

  const startListeningForOffer = () => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(pollSignals, 2000);
  };

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      cleanupCall();
    };
  }, []);

  const cleanupCall = () => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setActiveCall(null);
  };

  const createPeerConnection = (targetUserId) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pc.onicecandidate = async (e) => {
      if (e.candidate) {
        try {
          await axios.post(`${API}/live/rtc/signal`, {
            to_user: targetUserId,
            signal_type: 'ice',
            data: JSON.stringify(e.candidate)
          });
        } catch {}
      }
    };
    pc.ontrack = (e) => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = e.streams[0];
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        cleanupCall();
      }
    };
    pcRef.current = pc;
    return pc;
  };

  // Admin initiates call to approved student
  const initiateCall = async (targetUserId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const pc = createPeerConnection(targetUserId);
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await axios.post(`${API}/live/rtc/signal`, {
        to_user: targetUserId,
        signal_type: 'offer',
        data: JSON.stringify(offer)
      });
      setActiveCall(targetUserId);
      // Start polling for answer
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = setInterval(pollSignals, 2000);
      toast.success('Conectando audio...');
    } catch (err) {
      toast.error('Error al acceder al micrófono');
    }
  };

  // Student handles incoming offer
  const handleOffer = async (offer, fromUser) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const pc = createPeerConnection(fromUser);
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await axios.post(`${API}/live/rtc/signal`, {
        to_user: fromUser,
        signal_type: 'answer',
        data: JSON.stringify(answer)
      });
      setActiveCall(fromUser);
      toast.success('Audio conectado');
    } catch (err) {
      toast.error('Error al conectar audio');
    }
  };

  const handleAnswer = async (answer) => {
    if (pcRef.current && pcRef.current.signalingState === 'have-local-offer') {
      await pcRef.current.setRemoteDescription(new RTCSessionDescription(answer));
    }
  };

  const handleIce = async (candidate) => {
    if (pcRef.current) {
      try {
        await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      } catch {}
    }
  };

  const toggleHand = async () => {
    try {
      const res = await axios.post(`${API}/live/hand-raise`);
      setHandRaised(res.data.raised);
      if (!res.data.raised) cleanupCall();
    } catch { toast.error('Error'); }
  };

  const approveMic = async (userId) => {
    try {
      await axios.post(`${API}/live/mic-approve/${userId}`);
      // After approving, initiate WebRTC call
      setTimeout(() => initiateCall(userId), 1000);
    } catch { toast.error('Error'); }
  };

  const revokeMic = async (userId) => {
    try {
      await axios.post(`${API}/live/mic-revoke/${userId}`);
      if (activeCall === userId) cleanupCall();
    } catch { toast.error('Error'); }
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
      setMuted(!muted);
    }
  };

  const endCall = () => {
    cleanupCall();
    if (!isAdmin && handRaised) {
      toggleHand();
    }
  };

  const pendingRaises = raises.filter(r => r.status === 'pending');
  const approvedRaises = raises.filter(r => r.status === 'approved');

  return (
    <div className="glass-panel rounded-lg p-3" data-testid="hand-raise-panel">
      <audio ref={remoteAudioRef} autoPlay />
      
      {/* Student view */}
      {!isAdmin && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {activeCall ? (
              <>
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-green-400 text-xs font-bold">AUDIO ACTIVO</span>
              </>
            ) : handRaised ? (
              <>
                <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                <span className="text-yellow-400 text-xs">Esperando aprobación...</span>
              </>
            ) : (
              <span className="text-emerald-100/50 text-xs">Solicita hablar con el instructor</span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {activeCall && (
              <>
                <Button size="sm" onClick={toggleMute} variant="outline" className={`h-7 px-2 text-[11px] ${muted ? 'border-red-500 text-red-400' : 'border-emerald-500 text-emerald-400'}`} data-testid="mic-mute-btn">
                  {muted ? <MicOff className="h-3 w-3" /> : <Mic className="h-3 w-3" />}
                </Button>
                <Button size="sm" onClick={endCall} className="bg-red-500 text-white hover:bg-red-400 h-7 px-2 text-[11px]" data-testid="mic-end-btn">
                  <PhoneOff className="h-3 w-3" />
                </Button>
              </>
            )}
            {!activeCall && (
              <Button size="sm" onClick={toggleHand} variant="outline" className={`h-7 px-2 text-[11px] ${handRaised ? 'border-yellow-500 text-yellow-400 bg-yellow-500/10' : 'border-emerald-500 text-emerald-400'}`} data-testid="raise-hand-btn">
                <Hand className="h-3 w-3 mr-1" />{handRaised ? 'Bajar mano' : 'Levantar mano'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Admin view */}
      {isAdmin && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-emerald-50 font-bold text-xs" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              SOLICITUDES DE AUDIO ({pendingRaises.length + approvedRaises.length})
            </h3>
            {raises.length > 0 && (
              <button onClick={async () => { await axios.delete(`${API}/live/hand-raises`); cleanupCall(); }} className="text-emerald-100/30 hover:text-red-400 text-[10px]">Limpiar todo</button>
            )}
          </div>
          {pendingRaises.length === 0 && approvedRaises.length === 0 ? (
            <p className="text-emerald-100/30 text-xs">Sin solicitudes de audio</p>
          ) : (
            <div className="space-y-1.5">
              {approvedRaises.map(r => (
                <div key={r.user_id} className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded px-2 py-1.5" data-testid={`approved-mic-${r.user_id}`}>
                  <div className="flex items-center space-x-2">
                    <Mic className="h-3 w-3 text-green-400" />
                    <span className="text-green-400 text-xs font-bold">{r.user_name}</span>
                    {activeCall === r.user_id && <span className="text-green-300 text-[10px]">CONECTADO</span>}
                  </div>
                  <div className="flex items-center space-x-1">
                    {activeCall === r.user_id && (
                      <Button size="sm" onClick={toggleMute} variant="outline" className={`h-6 px-1.5 text-[10px] ${muted ? 'border-red-500 text-red-400' : 'border-green-500 text-green-400'}`}>
                        {muted ? <MicOff className="h-2.5 w-2.5" /> : <Mic className="h-2.5 w-2.5" />}
                      </Button>
                    )}
                    {!activeCall && (
                      <Button size="sm" onClick={() => initiateCall(r.user_id)} className="bg-green-500 text-black hover:bg-green-400 h-6 px-2 text-[10px]">Conectar</Button>
                    )}
                    <Button size="sm" onClick={() => revokeMic(r.user_id)} variant="outline" className="border-red-500/50 text-red-400 h-6 px-1.5 text-[10px]">
                      <PhoneOff className="h-2.5 w-2.5" />
                    </Button>
                  </div>
                </div>
              ))}
              {pendingRaises.map(r => (
                <div key={r.user_id} className="flex items-center justify-between bg-yellow-500/5 border border-yellow-500/20 rounded px-2 py-1.5" data-testid={`pending-mic-${r.user_id}`}>
                  <div className="flex items-center space-x-2">
                    <Hand className="h-3 w-3 text-yellow-400" />
                    <span className="text-yellow-400 text-xs">{r.user_name}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button size="sm" onClick={() => approveMic(r.user_id)} className="bg-emerald-500 text-black hover:bg-emerald-400 h-6 px-2 text-[10px]" data-testid={`approve-mic-${r.user_id}`}>Aprobar</Button>
                    <Button size="sm" onClick={() => revokeMic(r.user_id)} variant="outline" className="border-red-500/50 text-red-400 h-6 px-1.5 text-[10px]" data-testid={`deny-mic-${r.user_id}`}>
                      <X className="h-2.5 w-2.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LiveChat({ compact }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const chatEnd = useRef(null);
  const containerRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/live/chat`);
      setMessages(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  const isNearBottom = useRef(true);

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      isNearBottom.current = scrollHeight - scrollTop - clientHeight < 80;
    }
  };

  useEffect(() => {
    if (containerRef.current && isNearBottom.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    try {
      await axios.post(`${API}/live/chat`, { content: newMsg.trim() });
      setNewMsg('');
      fetchMessages();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const clearChat = async () => {
    if (!confirm('¿Limpiar todo el chat?')) return;
    await axios.delete(`${API}/live/chat`);
    fetchMessages();
  };

  const height = compact ? 'h-[250px]' : 'h-[400px] lg:h-[500px]';

  return (
    <div className={`glass-panel rounded-lg flex flex-col ${height}`} data-testid="live-chat">
      <div className="flex items-center justify-between px-4 py-2 border-b border-emerald-500/20 flex-shrink-0">
        <h3 className="text-emerald-50 font-bold text-xs" style={{ fontFamily: 'Rajdhani, sans-serif' }}>CHAT EN VIVO</h3>
        {user?.role === 'admin' && (
          <button onClick={clearChat} className="text-emerald-100/30 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
        )}
      </div>
      <div ref={containerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5" data-testid="chat-messages">
        {messages.length === 0 ? (
          <p className="text-emerald-100/30 text-xs text-center mt-6">Sin mensajes</p>
        ) : messages.map((msg) => (
          <div key={msg.id} data-testid={`chat-msg-${msg.id}`}>
            <div className="flex items-start space-x-1.5">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${msg.role === 'admin' ? 'bg-emerald-500/30 text-emerald-400' : 'bg-purple-500/20 text-purple-400'}`}>
                {msg.user_name?.charAt(0) || '?'}
              </div>
              <div className="min-w-0 flex-1">
                <span className={`text-[11px] font-bold ${msg.role === 'admin' ? 'text-emerald-400' : 'text-purple-300'}`}>
                  {msg.user_name}{msg.role === 'admin' && <span className="ml-1 text-[9px] bg-emerald-500/20 px-1 rounded">ADMIN</span>}
                </span>
                <p className="text-emerald-100/80 text-xs break-words leading-tight">{msg.content}</p>
              </div>
            </div>
          </div>
        ))}
        <div ref={chatEnd} />
      </div>
      <div className="px-2 py-2 border-t border-emerald-500/20 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <input type="text" value={newMsg} onChange={(e) => setNewMsg(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendMessage()} placeholder="Mensaje..." maxLength={200} className="flex-1 bg-black/40 border border-emerald-500/20 rounded-full px-3 py-1.5 text-xs text-emerald-50 placeholder:text-emerald-100/30 focus:outline-none focus:border-emerald-500/50" data-testid="chat-input" />
          <button onClick={sendMessage} disabled={!newMsg.trim()} className="text-emerald-400 hover:text-emerald-300 disabled:opacity-30" data-testid="chat-send"><Send className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}

export default function LiveTrading() {
  const { user } = useAuth();
  const scriptLoaded = useRef(false);
  const [isLive, setIsLive] = useState(false);
  const [source, setSource] = useState('mux');
  const [zoomLink, setZoomLink] = useState('');
  const [zoomInput, setZoomInput] = useState('');
  const [showSourceConfig, setShowSourceConfig] = useState(false);

  useEffect(() => {
    if (!scriptLoaded.current) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://cdn.jsdelivr.net/npm/@mux/mux-player';
      document.head.appendChild(script);
      scriptLoaded.current = true;
    }
    checkLiveStatus();
    const interval = setInterval(checkLiveStatus, 15000);
    return () => clearInterval(interval);
  }, []);

  // Wake Lock - keep screen on during live stream
  const wakeLockRef = useRef(null);
  useEffect(() => {
    const requestWakeLock = async () => {
      if (isLive && 'wakeLock' in navigator) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        } catch {}
      }
    };
    const releaseWakeLock = () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };

    if (isLive) {
      requestWakeLock();
      const handleVisibility = () => { if (document.visibilityState === 'visible' && isLive) requestWakeLock(); };
      document.addEventListener('visibilitychange', handleVisibility);
      return () => { releaseWakeLock(); document.removeEventListener('visibilitychange', handleVisibility); };
    } else {
      releaseWakeLock();
    }
  }, [isLive]);

  const checkLiveStatus = async () => {
    try {
      const res = await axios.get(`${API}/live/status`);
      setIsLive(res.data.is_live);
      setSource(res.data.source || 'mux');
      setZoomLink(res.data.zoom_link || '');
      setZoomInput(res.data.zoom_link || '');
    } catch {}
  };

  const notifyLive = async () => {
    try {
      await axios.post(`${API}/live/notify`);
      setIsLive(true);
      toast.success('Notificación enviada');
    } catch { toast.error('Error'); }
  };

  const toggleLive = async () => {
    try {
      const res = await axios.post(`${API}/live/status`);
      setIsLive(res.data.is_live);
    } catch {}
  };

  const saveSource = async (newSource) => {
    try {
      await axios.put(`${API}/live/source`, { source: newSource, zoom_link: zoomInput });
      setSource(newSource);
      setZoomLink(zoomInput);
      toast.success(newSource === 'mux' ? 'Modo: OBS Directo' : 'Modo: Zoom + OBS');
      setShowSourceConfig(false);
    } catch { toast.error('Error al guardar'); }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-[#020402]">
      <Navbar />
      <BrandWatermark />

      <div className="pt-16 sm:pt-20 pb-6 px-3 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="flex flex-row items-center justify-between mb-4 gap-2">
            <div className="min-w-0 flex-shrink">
              <div className="flex items-center space-x-2">
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isLive ? 'bg-red-500 animate-pulse' : 'bg-gray-500'}`} />
                <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold text-emerald-50 truncate" style={{ fontFamily: 'Rajdhani, sans-serif' }} data-testid="live-title">
                  {isLive ? 'EN VIVO' : 'OFF LINE'}
                </h1>
              </div>
            </div>
            <div className="flex flex-row items-center gap-1.5 flex-shrink-0 flex-nowrap">
              {isAdmin && (
                <>
                  <Button size="sm" onClick={() => setShowSourceConfig(!showSourceConfig)} variant="outline" className="text-[11px] px-2 py-1 h-7 border-purple-500/50 text-purple-400" data-testid="source-config-btn">
                    <Monitor className="h-3 w-3 mr-1" />Config
                  </Button>
                  <Button size="sm" onClick={toggleLive} variant="outline" className={`text-[11px] px-2 py-1 h-7 ${isLive ? 'border-gray-500 text-gray-400' : 'border-emerald-500 text-emerald-400'}`} data-testid="toggle-live-btn">
                    {isLive ? <><WifiOff className="h-3 w-3 mr-1" />Fin</> : <><Wifi className="h-3 w-3 mr-1" />Iniciar</>}
                  </Button>
                  <Button size="sm" onClick={notifyLive} className="bg-red-500 text-white hover:bg-red-400 text-[11px] px-2 py-1 h-7" data-testid="notify-live-btn">
                    <Radio className="h-3 w-3 mr-1" />Notificar
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Admin Source Config Panel - ONLY visible to admin */}
          {isAdmin && showSourceConfig && (
            <div className="glass-panel rounded-lg p-4 mb-4" data-testid="source-config-panel">
              <h3 className="text-emerald-50 font-bold text-sm mb-3" style={{ fontFamily: 'Rajdhani, sans-serif' }}>FUENTE DE TRANSMISIÓN</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                {/* OBS Direct */}
                <button
                  onClick={() => saveSource('mux')}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${source === 'mux' ? 'border-emerald-500 bg-emerald-500/10' : 'border-emerald-500/20 bg-black/30 hover:border-emerald-500/40'}`}
                  data-testid="source-mux-btn"
                >
                  <div className="flex items-center space-x-2 mb-1">
                    <Monitor className="h-4 w-4 text-emerald-400" />
                    <span className="text-emerald-50 font-bold text-sm">Mux (OBS)</span>
                  </div>
                  <p className="text-emerald-100/40 text-xs">Transmite desde OBS Studio</p>
                </button>

                {/* Zoom SDK */}
                <button
                  onClick={() => saveSource('zoom')}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${source === 'zoom' ? 'border-blue-500 bg-blue-500/10' : 'border-blue-500/20 bg-black/30 hover:border-blue-500/40'}`}
                  data-testid="source-zoom-btn"
                >
                  <div className="flex items-center space-x-2 mb-1">
                    <Video className="h-4 w-4 text-blue-400" />
                    <span className="text-emerald-50 font-bold text-sm">Zoom</span>
                  </div>
                  <p className="text-emerald-100/40 text-xs">Sesión embebida en la app</p>
                </button>
              </div>

              {source === 'zoom' && (
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-xs text-emerald-100/50">
                  Reunión configurada: <span className="text-blue-400 font-bold">858 5084 0264</span>
                  <br />Los estudiantes se unen automáticamente dentro de la app. No ven ningún enlace.
                </div>
              )}
            </div>
          )}

          {/* Video + Chat stacked vertically */}
          <div className="space-y-3">
            {/* Video / Zoom SDK / Offline */}
            <div>
              {isLive && source === 'zoom' ? (
                <ZoomEmbed userName={user?.name || user?.email?.split('@')[0] || 'Estudiante'} />
              ) : isLive ? (
                <div className="glass-panel rounded-lg overflow-hidden" data-testid="mux-player-container">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: `<mux-player
                        playback-id="${MUX_PLAYBACK_ID}"
                        metadata-video-title="Trading Alienígena Live"
                        stream-type="ll-live"
                        default-stream-type="ll-live"
                        target-live-window="0"
                        prefer-playback="ll-live"
                        live-edge-offset="2"
                        autoplay="any"
                        muted
                        max-resolution="1080p"
                        forward-seek-offset="0"
                        style="width:100%;aspect-ratio:16/9;display:block;">
                      </mux-player>`
                    }}
                  />
                </div>
              ) : (
                <div className="glass-panel rounded-lg overflow-hidden" data-testid="offline-banner">
                  <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    <img src="/offline_banner.png" alt="Off Line" className="absolute inset-0 w-full h-full object-contain bg-black" />
                  </div>
                </div>
              )}
            </div>

            {/* Hand Raise / Audio Controls */}
            <HandRaisePanel isAdmin={isAdmin} />

            {/* Chat below video */}
            <LiveChat compact={false} />

            {/* Stream Info */}
            <div className="glass-panel rounded-lg p-3 sm:p-5" data-testid="stream-info">
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <div className="bg-black/30 border border-emerald-500/20 rounded-lg p-2 sm:p-3">
                  <p className="text-emerald-100/60 text-[10px] sm:text-xs mb-0.5">Streamer</p>
                  <p className="text-emerald-400 font-bold text-xs sm:text-sm">Ricardopazfx</p>
                </div>
                <div className="bg-black/30 border border-emerald-500/20 rounded-lg p-2 sm:p-3">
                  <p className="text-emerald-100/60 text-[10px] sm:text-xs mb-0.5">Estado</p>
                  <p className={`font-bold text-xs sm:text-sm ${isLive ? 'text-red-400' : 'text-gray-400'}`}>{isLive ? 'EN VIVO' : 'OFF LINE'}</p>
                </div>
                <div className="bg-black/30 border border-emerald-500/20 rounded-lg p-2 sm:p-3">
                  <p className="text-emerald-100/60 text-[10px] sm:text-xs mb-0.5">Plataforma</p>
                  <p className="text-emerald-400 font-bold text-xs sm:text-sm">Trading Alienígena</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
